<template>
  <aside class="sidebar">
    <div class="sidebar-brand">
      <img :src="`${publicPath}logo.png`" alt="INVOXIS" class="brand-logo" />
    </div>

    <nav class="sidebar-nav">
      <div class="sidebar-section">Property Management</div>

      <router-link to="/dashboard" class="nav-item">
        <span class="nav-accent"></span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="3" width="7" height="7"/>
          <rect x="14" y="3" width="7" height="7"/>
          <rect x="3" y="14" width="7" height="7"/>
          <rect x="14" y="14" width="7" height="7"/>
        </svg>
        Dashboard
      </router-link>

      <router-link to="/reservations" class="nav-item">
        <span class="nav-accent"></span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="2" y="4" width="20" height="16" rx="2"/>
          <path d="M8 4v16M2 9h20"/>
        </svg>
        Reservations
      </router-link>

      <router-link to="/properties" class="nav-item">
        <span class="nav-accent"></span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        </svg>
        Properties
      </router-link>

      <div class="sidebar-section">Finance</div>

      <router-link to="/finance/month-end" class="nav-item">
        <span class="nav-accent"></span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="2" y="5" width="20" height="14" rx="2"/>
          <line x1="2" y1="10" x2="22" y2="10"/>
        </svg>
        Month-end
      </router-link>

      <router-link to="/finance/reports" class="nav-item">
        <span class="nav-accent"></span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M4 6h16M4 10h12M4 14h8"/>
          <rect x="2" y="2" width="20" height="20" rx="2"/>
        </svg>
        Reports
      </router-link>

      <router-link to="/finance/xero-settings" class="nav-item">
        <span class="nav-accent"></span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 2L2 7l10 5 10-5-10-5z"/>
          <path d="M2 17l10 5 10-5"/>
          <path d="M2 12l10 5 10-5"/>
        </svg>
        Finance
      </router-link>

      <div class="sidebar-section">Operations</div>

      <router-link to="/maintenance" class="nav-item">
        <span class="nav-accent"></span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 2v6M12 16v6M2 12h6M16 12h6" stroke-linecap="round"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
        Maintenance
      </router-link>

      <div class="sidebar-section">Account</div>

      <router-link to="/settings" class="nav-item">
        <span class="nav-accent"></span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="3"/>
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/>
        </svg>
        Settings
      </router-link>
    </nav>

    <div class="sidebar-user">
      <div class="sidebar-user-avatar">{{ userInitial }}</div>
      <div class="sidebar-user-info">
        <div class="sidebar-user-name">{{ username }}</div>
        <div class="sidebar-user-role">{{ userRole }}</div>
      </div>
    </div>
  </aside>
</template>

<script>
export default {
  name: 'Sidebar',
  data() {
    return {
      publicPath: process.env.BASE_URL,
      username: '',
      email: '',
      isSuperAdmin: false
    };
  },
  computed: {
    userInitial() {
      return (this.username && this.username.charAt(0).toUpperCase()) || 'U';
    },
    userRole() {
      return this.isSuperAdmin ? 'Administrator' : 'Member';
    }
  },
  mounted() {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      this.username = user.username || user.email || 'User';
      this.email = user.email || '';
      this.isSuperAdmin = !!user.is_super_admin;
    } catch (e) {
      this.username = 'User';
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600;700&display=swap');

*, *::before, *::after { box-sizing: border-box; }

.sidebar {
  width: 240px;
  flex-shrink: 0;
  background: linear-gradient(180deg, #5b58d4 0%, #4e4bbb 30%, #6b4fd8 70%, #7b52c8 100%);
  display: flex;
  flex-direction: column;
  padding: 0;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 100;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* ── BRAND ── */
.sidebar-brand {
  height: 60px;
  padding: 0 22px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-shrink: 0;
  position: relative;
}

.sidebar-brand::after {
  content: '';
  position: absolute;
  left: 22px;
  right: 22px;
  bottom: 0;
  height: 1px;
  background: linear-gradient(90deg,
    rgba(255,255,255,0) 0%,
    rgba(255,255,255,0.18) 50%,
    rgba(255,255,255,0) 100%);
}

.brand-logo {
  height: 18px;
  width: auto;
  object-fit: contain;
  filter: brightness(0) invert(1);
  display: block;
}

/* ── NAV ── */
.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  padding: 6px 0 12px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255,255,255,0.15) transparent;
}

.sidebar-nav::-webkit-scrollbar {
  width: 4px;
}

.sidebar-nav::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.15);
  border-radius: 4px;
}

.sidebar-section {
  padding: 18px 20px 6px;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(255,255,255,0.42);
  font-family: 'Bricolage Grotesque', sans-serif;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  margin: 2px 10px;
  border-radius: 9px;
  font-size: 13.5px;
  font-weight: 500;
  color: rgba(255,255,255,0.68);
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease;
  position: relative;
  background: transparent;
  border: none;
  text-align: left;
  font-family: inherit;
}

.nav-accent {
  position: absolute;
  left: -10px;
  top: 50%;
  transform: translateY(-50%) scaleY(0.2);
  width: 3px;
  height: 22px;
  border-radius: 0 3px 3px 0;
  background: white;
  opacity: 0;
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.nav-item:hover {
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.95);
}

.nav-item.router-link-active {
  background: rgba(255,255,255,0.14);
  color: #ffffff;
  font-weight: 600;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.08);
}

.nav-item.router-link-active .nav-accent {
  opacity: 1;
  transform: translateY(-50%) scaleY(1);
}

.nav-item svg {
  opacity: 0.75;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  transition: opacity 0.15s ease;
}

.nav-item:hover svg {
  opacity: 0.95;
}

.nav-item.router-link-active svg {
  opacity: 1;
}

/* ── USER BLOCK ── */
.sidebar-user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  margin: 8px 10px 14px;
  border-radius: 12px;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.1);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  flex-shrink: 0;
  position: relative;
}

.sidebar-user::before {
  content: '';
  position: absolute;
  top: -1px;
  left: 20px;
  right: 20px;
  height: 1px;
  background: linear-gradient(90deg,
    rgba(255,255,255,0) 0%,
    rgba(255,255,255,0.2) 50%,
    rgba(255,255,255,0) 100%);
}

.sidebar-user-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.16) 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  font-family: 'Bricolage Grotesque', sans-serif;
  letter-spacing: -0.3px;
  flex-shrink: 0;
  border: 1px solid rgba(255,255,255,0.2);
}

.sidebar-user-info {
  flex: 1;
  min-width: 0;
}

.sidebar-user-name {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 12.5px;
  font-weight: 700;
  color: white;
  letter-spacing: -0.2px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-user-role {
  font-size: 10.5px;
  color: rgba(255,255,255,0.5);
  margin-top: 2px;
  font-weight: 500;
  letter-spacing: 0.02em;
}
</style>
